import { Done as DoneIcon } from "@mui/icons-material";
import {
  Card,
  CardContent,
  CircularProgress,
  Fade,
  FormHelperText,
  Grid,
  Typography,
} from "@mui/material";
import { AsolviSignoff } from "components/AsolviSignoff";
import { AsolviSwitch } from "components/AsolviSwitch";
import BackdropWhite from "components/BackdropWhite";
import { JobVisitSummary } from "components/job/completion/JobVisitSummary";
import { Signoff } from "components/job/completion/Signoff";
import PrimaryButton from "components/PrimaryButton";
import StyledTextField from "components/StyledTextField";
import technicianSettings from "helpers/technicianSettings";
import { Job } from "models/Job";
import { JobVisitType } from "models/JobVisitType";
import { FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";

interface SignoffDialogProps {
  job: Job;
  jobVisit: JobVisitType;
  onSetVisitValue: (key: "followUp" | "solutionDescription", value: any) => void;
  handleSignature: (value: string | undefined) => void;
  disableNextButton: boolean;
  loading: boolean;
  openCustomerReview: () => void;
}

export const TechnicianSignoffComponent: FC<SignoffDialogProps> = (props: SignoffDialogProps) => {
  const {
    job,
    jobVisit,
    disableNextButton,
    loading,
    openCustomerReview,
    handleSignature,
    onSetVisitValue,
  } = props;
  const intl = useIntl();
  const { solutionDescription, followUp, errors, signatureEngineer } = jobVisit;
  const requireEngineerSignature = technicianSettings.requireTechnicianSignature ?? false;
  const storeSignaturesLocally = technicianSettings.storeSignaturesLocal ?? false;

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item sx={{ width: "100%" }}>
        <JobVisitSummary jobVisit={jobVisit} />
      </Grid>
      <Grid item container alignItems="center" justifyContent="space-between">
        <Grid item>
          <Typography variant="body2">
            <FormattedMessage id="visit.followUp.required" />
          </Typography>
        </Grid>
        <Grid item>
          <AsolviSwitch
            key={`FollowUpSwitch`}
            checked={followUp.followUpChecked}
            onChange={() =>
              onSetVisitValue("followUp", {
                ...followUp,
                followUpChecked: !followUp.followUpChecked,
              })
            }
            data-testid="TechnicianSignoffDialog-FollowUpButton"
          />
        </Grid>
      </Grid>
      {followUp.followUpChecked && (
        <Fade in={followUp.followUpChecked}>
          <Grid item container alignItems="center" justifyContent="space-between">
            <Grid item width="100%" marginTop="16px">
              <StyledTextField
                id="reason"
                name="reason"
                label={intl.formatMessage({ id: "visit.followUp.reason" })}
                value={solutionDescription}
                onChange={({ target: { value } }) => {
                  onSetVisitValue("solutionDescription", value);
                }}
                multiline
                rows={10}
              />
              {solutionDescription.length === 0 &&
                technicianSettings?.requiredSolutionDescription && (
                  <FormHelperText error>
                    {intl.formatMessage({
                      id: "visit.followUp.reasonRequired",
                    })}
                  </FormHelperText>
                )}
            </Grid>
          </Grid>
        </Fade>
      )}

      {requireEngineerSignature && (
        <Grid item sx={{ width: "100%" }}>
          {storeSignaturesLocally ? (
            <Signoff
              externalId={job.externalId}
              handleSignature={handleSignature}
              error={errors.engineerSignature}
              label={intl.formatMessage({ id: "visit.engineerSignature" })}
              signatureData={signatureEngineer}
            />
          ) : (
            <Card elevation={1}>
              <CardContent>
                <AsolviSignoff
                  handleSignature={handleSignature}
                  labelText={intl.formatMessage({ id: "visit.engineerSignature" })}
                />
              </CardContent>
            </Card>
          )}
        </Grid>
      )}
      <Grid item pb={1.5}>
        <BackdropWhite open={loading}>
          <CircularProgress color="inherit" />
        </BackdropWhite>

        <PrimaryButton
          key={`to-customer-summary-button`}
          startIcon={<DoneIcon />}
          variant="contained"
          onClick={openCustomerReview}
          fullWidth
          disabled={disableNextButton}
        >
          <FormattedMessage id="general.next" />
        </PrimaryButton>
      </Grid>
    </Grid>
  );
};
